/**
 * UserDashboardAllObjectiveDrawerMainContent
 *
 * Tabbed view of Objectives, Key Results, Initiatives, and Milestones for a user.
 * Fetches lists per tab, computes counts, and supports editing entities via
 * delegated API calls. Formats list data for display components and manages
 * selection across levels.
 */
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import InitiativesStatus from "./initiatives-status";
import "./user-dashboard-all-objective-drawer-main-content.scss";
import Status from "../../../request-dashboard/request/status/status";
import UserDashboardObjectiveList from "./user-dashboard-listing/user-dashboard-objective-list/user-dashboard-objective-list";
import UserDashboardInitiativeList from "./user-dashboard-listing/user-dashboard-initiative-list/user-dashboard-initiative-list";
import UserDashboardKeyresultList from "./user-dashboard-listing/user-dashboard-keyresult-list/user-dashboard-keyresult-list";
import UserDashboardMilestoneList from "./user-dashboard-listing/user-dashboard-milestone-list/user-dashboard-milestone-list";
import { updateInitiative } from "../../../../api/initiative";
import { useSetLoading } from "../../../../common/hooks/appearance";
import WarningConfirmationPopup from "../../../../common/components/warning-confirmation-popup/warning-confirmation-popup";
import { editKeyresult, updateObjective } from "../../../../api/objectives";
import {
  getEmployeeInitiatives,
  getEmployeeKeyResults,
  getEmployeeMilestoneDepartments,
  getEmployeeObjectives,
} from "../../../../api/user-dashboard";
import dayjs from "dayjs";
import { routes } from "../../../../constants/routes";
import { Avatar, AvatarGroup, Tooltip } from "@mui/material";
import Department from "../../../departments/departments";

const UserDashboardAllObjectiveDrawerMainContent: any = (props: any) => {
  const {
    approverId,
    employeesList,
    statusListKeyResults,
    statusListObjectives,
    statusListInitiatives,
    setAlertMessage,
    setAlertType,
    setAlertOpen,
    setEmployeeId
  } = props;
  const navigate = useNavigate();
  const [tabData, setTabData] = useState([
    {
      label: "Objectives",
      apiFn: getEmployeeObjectives,
      countKey: "listOfObjectives",
      listKey: "objectivesResponse",
      count: 0,
    },
    {
      label: "Key Results",
      apiFn: getEmployeeKeyResults,
      countKey: "listOfKeyResults",
      listKey: "keyResultsResponse",
      count: 0,
    },
    {
      label: "Initiatives",
      apiFn: getEmployeeInitiatives,
      countKey: "listOfInitiatives", // ← match exactly with API
      listKey: "listOfinitiaves",
      count: 0,
    },
  ]);

  const [activeTab, setActiveTab] = useState(tabData[0]);
  const [tabListData, setTabListData] = useState<any[]>([]);
  const [selectedObjective, setSelectedObjective] = useState<any>(null);
  const [selectedKeyResult, setSelectedKeyResult] = useState<any>(null);
  const [selectedInitiative, setSelectedInitiative] = useState<any>(null);
  const [selectedMilestone, setSelectedMilestone] = useState<any>(null);
  const [objectivesData, setObjectivesData] = useState<any[]>([]);
  const [keyResultsData, setKeyResultsData] = useState<any[]>([]);
  const [initiativesData, setInitiativesData] = useState<any[]>([]);
  const [milestonesData, setMilestonesData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isWarningOpen, setIsWarningOpen] = useState(false);

  const handleEditObjective = async (updatedObj: any) => {
    setLoading(true);
    try {
      const payload = {
        objectiveName: updatedObj.objectiveName,
        objectiveDescription: updatedObj.objectiveDescription,
        startDate: updatedObj.startDate,
        endDate: updatedObj.endDate,
        status: updatedObj.status,
        ownerIds: updatedObj.ownerIds,
        empty: false,
      };

      await updateObjective(updatedObj.id, payload);

      setAlertMessage("Description added successfully");
      setAlertType("success");
      setAlertOpen(true);
      setTimeout(() => setLoading(false), 0);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to update description",
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };
  const handleEditKeyResult = async (krObj: any) => {
    setLoading(true);
    try {
      const payload = {
        ownerIds: krObj.ownerIds,
        title: krObj.title,
        description: krObj.description,
        weightage: krObj.weightage,
        status: krObj.status,
        startDate: krObj.startDate,
        endDate: krObj.endDate,
        comments: krObj.comments,
      };
      await editKeyresult(krObj.pk, payload);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  const handleEditInitiative = async (formValues: any) => {
    setLoading(true);
    try {
      const payload = {
        description: formValues.description,
        ownerIds: formValues.ownerId || [],
        startDate: formValues.startDate,
        endDate: formValues.endDate,
        departmentIds: formValues.departmentIds,
        title: formValues.title,
        status: formValues?.status?.statusName || "",
        weightage: formValues.weightage,
        participatingDepartments: formValues.participatingDepartments,
      };

      await updateInitiative(formValues.id, payload);
      setAlertMessage("Initiative updated successfully");
      setAlertType("success");
      setAlertOpen(true);
      setTimeout(() => setLoading(false), 0);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to update Initiative",
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };
  const [anchorElOwner, setAnchorElOwner] = React.useState<{
    [key: string]: HTMLElement | null;
  }>({});
  const [milestoneDepartments, setMilestoneDepartments] = useState<any[]>([]);

  const handleOpenOwnerMenu = (
    event: React.MouseEvent<HTMLElement>,
    objectiveId: string,
  ) => {
    // Toggle behavior: if menu is already open for this objective, close it
    if (anchorElOwner[objectiveId]) {
      setAnchorElOwner((prev) => ({
        ...prev,
        [objectiveId]: null,
      }));
    } else {
      setAnchorElOwner((prev) => {
        const newState = {
          ...prev,
          [objectiveId]: event.currentTarget,
        };
        return newState;
      });
    }
  };

  const handleCloseOwnerMenu = (objectiveId: string, ownerId?: string) => {
    setAnchorElOwner((prev) => ({
      ...prev,
      [objectiveId]: null,
    }));
    setEmployeeId(ownerId ? Number(ownerId) : null);
  };

  // keep your stringAvatar
  const stringAvatar = (name: string, color?: string, styles?: any) => {
    return {
      sx: {
        ...styles,
        bgcolor: color || "#403c6f",
      },
      children:
        name?.split(" ")?.length > 1
          ? `${name?.split(" ")?.[0]?.[0]}${name?.split(" ")?.[1]?.[0]}`
          : `${name?.[0] || ""}`,
    };
  };
  const formatObjectives = (objectives: any[]) => {
    return objectives.map((item, index) => {
      const objectiveId = String(
        item.objectiveId ?? item.id ?? `objective-${index}`,
      );
      const allOwners =
        item.owners?.flatMap((owner: any) => owner.assignedTo || []) || [];

      return {
        level: "Objectives",
        title: item.objectiveTitle || "-",
        assignedTo: (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <AvatarGroup max={3} spacing={10}>
              {[allOwners?.[0]]?.map((owner: any, index: number) => (
                <Tooltip title={owner?.employeeFullName || ""} key={index + 1}>
                  <Avatar
                    alt={owner?.employeeFullName}
                    src={owner?.employeeImage}
                    {...(!owner?.employeeImage &&
                      stringAvatar(owner?.employeeFullName))}
                    style={{ cursor: "pointer" }}
                  />
                </Tooltip>
              ))}
              {allOwners?.length > 1 && (
                <Avatar
                  sx={{
                    bgcolor: "#403c6f",
                    fontWeight: "normal",
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: "#2c2856",
                    },
                  }}
                  onClick={(event) => {
                    handleOpenOwnerMenu(event, objectiveId);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  {allOwners?.length - 1}
                </Avatar>
              )}
            </AvatarGroup>
          </div>
        ),
        timeline: item.startDate ? dayjs(item.startDate).format("YYYY") : "-",
        progress: `${Math.round(item.progress) || 0}%`,
        status: <Status status={item.status || "-"} />,
        data: item,
      };
    });
  };

  const formatKeyResults = (keyResults: any[]) => {
    return keyResults.map((item, index) => {
      // Use index-based ID for consistency between format and render functions
      const keyResultId = String(item.pk ?? item.id ?? `keyresult-${index}`);
      const allOwners = item.assignedTo || [];

      return {
        level: "Key Results", // tab name
        title: item.title || "-",
        assignedTo: (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <AvatarGroup max={3} spacing={10}>
              {[allOwners?.[0]]?.map((owner: any, index: number) => (
                <Tooltip title={owner?.employeeFullName || ""} key={index + 1}>
                  <Avatar
                    alt={owner?.employeeFullName}
                    src={owner?.employeeImage}
                    {...(!owner?.employeeImage &&
                      stringAvatar(owner?.employeeFullName))}
                    style={{ cursor: "pointer" }}
                  />
                </Tooltip>
              ))}
              {allOwners?.length > 1 && (
                <Avatar
                  sx={{
                    bgcolor: "#403c6f",
                    fontWeight: "normal",
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: "#2c2856",
                    },
                  }}
                  onClick={(event) => {
                    handleOpenOwnerMenu(event, keyResultId);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  {allOwners?.length - 1}
                </Avatar>
              )}
            </AvatarGroup>
          </div>
        ),
        startDate: item.startDate
          ? dayjs(item.startDate).format("MM-DD-YYYY")
          : "-",
        endDate: item.endDate ? dayjs(item.endDate).format("MM-DD-YYYY") : "-",
        progress: `${Math.round(item.progress) || 0}%`,
        status: <Status status={item.status || "-"} />,
        data: item,
      };
    });
  };
  const formatInitiatives = (initiatives: any[]) => {
    return initiatives.map((item, index) => {
      const initiativeId = String(
        item.initiativeId ?? item.id ?? `initiative-${index}`,
      );
      const allOwners = item.assignedTo || [];

      return {
        level: "Initiatives",
        title: item.initiativeTitle || "-",
        assignedTo: (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <AvatarGroup max={3} spacing={10}>
              {[allOwners?.[0]]?.map((owner: any, index: number) => (
                <Tooltip title={owner?.employeeFullName || ""} key={index + 1}>
                  <Avatar
                    alt={owner?.employeeFullName}
                    src={owner?.employeeImage}
                    {...(!owner?.employeeImage &&
                      stringAvatar(owner?.employeeFullName))}
                    style={{ cursor: "pointer" }}
                  />
                </Tooltip>
              ))}
              {allOwners?.length > 1 && (
                <Avatar
                  sx={{
                    bgcolor: "#403c6f",
                    fontWeight: "normal",
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: "#2c2856",
                    },
                  }}
                  onClick={(event) => {            
                    handleOpenOwnerMenu(event, initiativeId);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  {allOwners?.length - 1}
                </Avatar>
              )}
            </AvatarGroup>
          </div>
        ),
        startDate: item.initiativeStartDate
          ? dayjs(item.initiativeStartDate).format("MM-DD-YYYY")
          : "-",
        endDate: item.initiativeEndDate
          ? dayjs(item.initiativeEndDate).format("MM-DD-YYYY")
          : "-",
        progress: `${Math.round(item.progress) || 0}%`,
        status: <Status status={item.status || "-"} />,
        data: item,
      };
    });
  };

  const formatMilestones = (milestones: any[]) => {
    return milestones.map((item, index) => {
      const milestoneId = String(
        item.milestoneId ?? item.id ?? `milestone-${index}`,
      );
      const employee = item.employeeDetailResponse;
      // Check if assignedTo exists (for multiple owners), otherwise use single employee
      const allOwners = item.assignedTo || (employee ? [employee] : []);

      return {
        id: item?.milestoneId,
        level: "Milestones",
        title: item.milestoneTitle || "-",
        department: (
          <Department
            key={item?.departmentResponse?.departmentId}
            departmentId={item?.departmentResponse?.departmentId}
            departmentName={item?.departmentResponse?.departmentName || "-"}
          />
        ),
        date: item.startDate ? dayjs(item.startDate).format("MM-DD-YYYY") : "-",
        status: (
          <Status status={item.statusResponse?.statusName || "Not Started"} />
        ),
        assignedTo:
          allOwners.length > 0 ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <AvatarGroup max={3} spacing={10}>
                {[allOwners?.[0]]?.map((owner: any, index: number) => (
                  <Tooltip
                    title={owner?.employeeFullName || ""}
                    key={index + 1}
                  >
                    <Avatar
                      alt={owner?.employeeFullName}
                      src={owner?.employeeImage}
                      {...(!owner?.employeeImage &&
                        stringAvatar(owner?.employeeFullName))}
                      style={{ cursor: "pointer" }}
                    />
                  </Tooltip>
                ))}
                {allOwners?.length > 1 && (
                  <Avatar
                    sx={{
                      bgcolor: "#403c6f",
                      fontWeight: "normal",
                      cursor: "pointer",
                      "&:hover": {
                        bgcolor: "#2c2856",
                      },
                    }}
                    onClick={(event) => {
                      handleOpenOwnerMenu(event, milestoneId);
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    {allOwners?.length - 1}
                  </Avatar>
                )}
              </AvatarGroup>
            </div>
          ) : (
            "-"
          ),
        data: item,
      };
    });
  };

  const fetchTabCounts = async (employeeId: number) => {
    try {
      const updated = await Promise.all(
        tabData.map(async (tab) => {
          const res = await tab.apiFn(employeeId);
          return {
            ...tab,
            count: res?.data?.data?.[tab.countKey] ?? 0,
          };
        }),
      );
      setTabData(updated);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchMilestoneDepartments = async (employeeId: number) => {
    try {
      const response = await getEmployeeMilestoneDepartments(employeeId);
      setMilestoneDepartments(response.data.data);
    } catch (err) {
      console.error(err);
    }
  };
  useEffect(() => {
    if (approverId) {
      fetchTabCounts(approverId);
      fetchMilestoneDepartments(approverId);
    }
  }, [approverId]);

  const fetchTabListData = async (employeeId: number, tab: any) => {
    try {
      setLoading(true);
      const res = await tab.apiFn(employeeId);
      let formatted: any[] = [];

      if (tab.label === "Objectives") {
        const list = res?.data?.data?.[tab.listKey] ?? [];
        setObjectivesData(list); // Store raw data for menu rendering
        formatted = formatObjectives(list);
      } else if (tab.label === "Key Results") {
        const list = res?.data?.data?.[tab.listKey] ?? [];
        setKeyResultsData(list); // Store raw data for menu rendering
        formatted = formatKeyResults(list);
      } else if (tab.label === "Initiatives") {
        const list = res?.data?.data?.[tab.listKey] ?? [];
        setInitiativesData(list); // Store raw data for menu rendering
        formatted = formatInitiatives(list);
      } else if (tab.label === "Milestones") {
        const list = res?.data?.data?.[tab.listKey] ?? [];
        setMilestonesData(list); // Store raw data for menu rendering
        formatted = formatMilestones(list);
      } else {
        // For other tabs, use your existing formatting
        const list = res?.data?.[tab.listKey] ?? [];
        formatted = list.map((item: any) => ({
          id: item.id,
          title: item.title || "-",
          timeline: `${item.start_date || "-"} - ${item.end_date || "-"}`,
          percentageComplete: item.percentageComplete || "-",
          status: <InitiativesStatus status={item.status} />,
          data: item,
        }));
      }

      setTabListData(formatted);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false); // stop loading
    }
  };

  useEffect(() => {
    if (approverId && activeTab) {
      fetchTabListData(approverId, activeTab);
    }
  }, [approverId, activeTab]);

  // Handle click outside to close menus
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      // Check if any menu is open
      const hasOpenMenus = Object.values(anchorElOwner).some(
        (el) => el !== null,
      );
      if (hasOpenMenus) {
        // Check if click is outside all menus and trigger elements
        const isClickOutside =
          !target.closest("[data-owner-menu]") &&
          !target.closest(".MuiAvatar-root");
        if (isClickOutside) {
          setAnchorElOwner({});
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [anchorElOwner]);

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  const renderTabContent = () => {
    switch (activeTab.label) {
      case "Objectives":
        return (
          <UserDashboardObjectiveList
            data={tabListData}
            onRowClick={(rowData: any, columnKey?: string) => {
              if (columnKey === "1") {
                setSelectedObjective(rowData);
              }else {
                const queryParams = new URLSearchParams();
                queryParams.set("dashboardTab", "Objectives");
                queryParams.set("objectiveId", String(rowData.data.objectiveId));
                navigate(`${routes.COMPANYDASHBOARD}?${queryParams.toString()}`);
              }
            }}
            selectedObjective={selectedObjective}
            setSelectedObjective={setSelectedObjective}
            employeesList={employeesList}
            statusListObjectives={statusListObjectives}
            handleEditObjective={handleEditObjective}
          />
        );
      case "Key Results":
        return (
          <UserDashboardKeyresultList
            onRowClick={(rowData: any, columnKey?: string) => {
              if (columnKey === "1") {
                setSelectedKeyResult(rowData);
              } else 
                if (rowData.data.objectiveIds?.length){
                  const queryParams = new URLSearchParams();
                  queryParams.set("dashboardTab", "Objectives");
                  queryParams.set("objectiveId", String(rowData.data.objectiveIds?.[0]));
                  navigate(`${routes.COMPANYDASHBOARD}?${queryParams.toString()}`);
                } else {
                  setIsWarningOpen(true);
                }
            }}
            data={tabListData}
            selectedKeyResult={selectedKeyResult}
            setSelectedKeyResult={setSelectedKeyResult}
            employeesList={employeesList}
            statusListKeyResults={statusListKeyResults}
            handleEditKeyResult={handleEditKeyResult}
          />
        );
      case "Initiatives":
        return (
          <UserDashboardInitiativeList
            onRowClick={(rowData: any, columnKey?: string) => {
              if (columnKey === "1") {
                setSelectedInitiative(rowData);
              }else {
              const queryParams = new URLSearchParams();
              queryParams.set("dashboardTab", "Initiatives");
              queryParams.set("initiativeId", String(rowData.data.initiativeId));
              navigate(`${routes.COMPANYDASHBOARD}?${queryParams.toString()}`);
              }
            }}
            data={tabListData}
            selectedInitiative={selectedInitiative}
            setSelectedInitiative={setSelectedInitiative}
            employeesList={employeesList}
            statusListInitiatives={statusListInitiatives}
            handleEditInitiative={handleEditInitiative}
          />
        );
      case "Milestones":
        return (
          <UserDashboardMilestoneList
            onRowClick={(rowData: any) => {
              setSelectedMilestone(rowData);
            }}
            data={tabListData}
            selectedMilestone={selectedMilestone}
            setSelectedMilestone={setSelectedMilestone}
            departmentsList={milestoneDepartments}
          />
        );
      default:
        return null;
    }
  };

  // Render menus for objectives outside of table formatting
  const renderObjectiveMenus = () => {
    return objectivesData.map((item, index) => {
      const objectiveId = String(
        item.objectiveId ?? item.id ?? `objective-${index}`,
      );
      const allOwners =
        item.owners?.flatMap((owner: any) => owner.assignedTo || []) || [];

      if (allOwners.length <= 1 || !anchorElOwner[objectiveId]) return null;

      const anchorRect = anchorElOwner[objectiveId]?.getBoundingClientRect();
      if (!anchorRect) return null;

      return (
        <div
          key={`menu-${objectiveId}`}
          style={{
            position: "fixed",
            top: anchorRect.bottom + 5,
            left: anchorRect.left,
            zIndex: 99999,
            backgroundColor: "white",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            padding: "8px",
            display: "flex",
            flexDirection: "row",
            gap: "8px",
            alignItems: "center",
            maxWidth: "300px",
            flexWrap: "wrap",
          }}
        >
          {allOwners?.map((owner: any, index: number) => (
            <Tooltip
              key={index + 1}
              title={owner?.employeeFullName || "Unknown"}
              placement="top"
            >
              <Avatar
                alt={owner?.employeeFullName}
                src={owner?.employeeImage}
                {...(!owner?.employeeImage &&
                  stringAvatar(owner?.employeeFullName, "#403c6f"))} // Blue color for initials
                style={{
                  cursor: "pointer",
                  width: 36,
                  height: 36,
                  transition: "transform 0.2s",
                  fontFamily: "Manrope, sans-serif",
                  fontSize: "14px",
                }}
                sx={{
                  bgcolor: owner?.employeeImage ? "transparent" : "#403c6f", // Blue background when no image
                  color: "#ffffff",
                  fontFamily: "Manrope, sans-serif",
                  fontSize: "14px",
                  "&:hover": {
                    transform: "scale(1.1)",
                    bgcolor: owner?.employeeImage ? "transparent" : "#403c6f", // Darker blue on hover
                  },
                }}
                onClick={() => // NOSONAR
                  handleCloseOwnerMenu(objectiveId, owner?.employeeId)
                }
              />
            </Tooltip>
          ))}
        </div>
      );
    });
  };

  // Render menus for key results
  const renderKeyResultMenus = () => {
    return keyResultsData.map((item, index) => {
      // Use the same ID generation as in formatKeyResults
      const keyResultId = String(item.pk ?? item.id ?? `keyresult-${index}`);
      const allOwners = item.assignedTo || [];

      if (allOwners.length <= 1 || !anchorElOwner[keyResultId]) return null;

      const anchorRect = anchorElOwner[keyResultId]?.getBoundingClientRect();
      if (!anchorRect) return null;

      return (
        <div
          key={`menu-${keyResultId}`}
          style={{
            position: "fixed",
            top: anchorRect.bottom + 5,
            left: anchorRect.left,
            zIndex: 99999,
            backgroundColor: "white",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            padding: "8px",
            display: "flex",
            flexDirection: "row",
            gap: "8px",
            alignItems: "center",
            maxWidth: "300px",
            flexWrap: "wrap",
          }}
        >
          {allOwners?.map((owner: any, index: number) => (
            <Tooltip
              key={index + 1}
              title={owner?.employeeFullName || "Unknown"}
              placement="top"
            >
              <Avatar
                alt={owner?.employeeFullName}
                src={owner?.employeeImage}
                {...(!owner?.employeeImage &&
                  stringAvatar(owner?.employeeFullName, "#403c6f"))}
                style={{
                  cursor: "pointer",
                  width: 36,
                  height: 36,
                  transition: "transform 0.2s",
                  fontFamily: "Manrope, sans-serif",
                  fontSize: "12px",
                }}
                sx={{
                  bgcolor: owner?.employeeImage ? "transparent" : "#403c6f",
                  color: "#ffffff",
                  fontFamily: "Manrope, sans-serif",
                  fontSize: "12px",
                  fontWeight: 600,
                  "&:hover": {
                    transform: "scale(1.1)",
                    bgcolor: owner?.employeeImage ? "transparent" : "#2c2856",
                  },
                }}
                onClick={() =>// NOSONAR
                  handleCloseOwnerMenu(keyResultId, owner?.employeeId)
                }
              />
            </Tooltip>
          ))}
        </div>
      );
    });
  };

  // Render menus for initiatives
  const renderInitiativeMenus = () => {
    return initiativesData.map((item, index) => {
      const initiativeId = String(
        item.initiativeId ?? item.id ?? `initiative-${index}`,
      );
      const allOwners = item.assignedTo || [];

      if (allOwners.length <= 1 || !anchorElOwner[initiativeId]) return null;

      const anchorRect = anchorElOwner[initiativeId]?.getBoundingClientRect();
      if (!anchorRect) return null;

      return (
        <div
          key={`menu-${initiativeId}`}
          style={{
            position: "fixed",
            top: anchorRect.bottom + 5,
            left: anchorRect.left,
            zIndex: 99999,
            backgroundColor: "white",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            padding: "8px",
            display: "flex",
            flexDirection: "row",
            gap: "8px",
            alignItems: "center",
            maxWidth: "300px",
            flexWrap: "wrap",
          }}
        >
          {allOwners?.map((owner: any, index: number) => (
            <Tooltip
              key={index + 1}
              title={owner?.employeeFullName || "Unknown"}
              placement="top"
            >
              <Avatar
                alt={owner?.employeeFullName}
                src={owner?.employeeImage}
                {...(!owner?.employeeImage &&
                  stringAvatar(owner?.employeeFullName, "#403c6f"))}
                style={{
                  cursor: "pointer",
                  width: 36,
                  height: 36,
                  transition: "transform 0.2s",
                  fontFamily: "Manrope, sans-serif",
                  fontSize: "12px",
                }}
                sx={{
                  bgcolor: owner?.employeeImage ? "transparent" : "#403c6f",
                  color: "#ffffff",
                  fontFamily: "Manrope, sans-serif",
                  fontSize: "12px",
                  fontWeight: 600,
                  "&:hover": {
                    transform: "scale(1.1)",
                    bgcolor: owner?.employeeImage ? "transparent" : "#2c2856",
                  },
                }}
                onClick={() =>// NOSONAR
                  handleCloseOwnerMenu(initiativeId, owner?.employeeId)
                }
              />
            </Tooltip>
          ))}
        </div>
      );
    });
  };

  // Render menus for milestones
  const renderMilestoneMenus = () => {
    return milestonesData.map((item, index) => {
      const milestoneId = String(
        item.milestoneId ?? item.id ?? `milestone-${index}`,
      );
      const employee = item.employeeDetailResponse;
      const allOwners = item.assignedTo || (employee ? [employee] : []);

      if (allOwners.length <= 1 || !anchorElOwner[milestoneId]) return null;

      const anchorRect = anchorElOwner[milestoneId]?.getBoundingClientRect();
      if (!anchorRect) return null;

      return (
        <div
          key={`menu-${milestoneId}`}
          style={{
            position: "fixed",
            top: anchorRect.bottom + 5,
            left: anchorRect.left,
            zIndex: 99999,
            backgroundColor: "white",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            padding: "8px",
            display: "flex",
            flexDirection: "row",
            gap: "8px",
            alignItems: "center",
            maxWidth: "300px",
            flexWrap: "wrap",
          }}
        >
          {allOwners?.map((owner: any, index: number) => (
            <Tooltip
              key={index + 1}
              title={owner?.employeeFullName || "Unknown"}
              placement="top"
            >
              <Avatar
                alt={owner?.employeeFullName}
                src={owner?.employeeImage}
                {...(!owner?.employeeImage &&
                  stringAvatar(owner?.employeeFullName, "#403c6f"))}
                style={{
                  cursor: "pointer",
                  width: 36,
                  height: 36,
                  transition: "transform 0.2s",
                  fontFamily: "Manrope, sans-serif",
                  fontSize: "12px",
                }}
                sx={{
                  bgcolor: owner?.employeeImage ? "transparent" : "#403c6f",
                  color: "#ffffff",
                  fontFamily: "Manrope, sans-serif",
                  fontSize: "12px",
                  fontWeight: 600,
                  "&:hover": {
                    transform: "scale(1.1)",
                    bgcolor: owner?.employeeImage ? "transparent" : "#2c2856",
                  },
                }}
                onClick={() =>// NOSONAR
                  handleCloseOwnerMenu(milestoneId, owner?.employeeId)
                }
              />
            </Tooltip>
          ))}
        </div>
      );
    });
  };

  return (
    <>
      {/* Render menus based on active tab */}
      {activeTab.label === "Objectives" && renderObjectiveMenus()}
      {activeTab.label === "Key Results" && renderKeyResultMenus()}
      {activeTab.label === "Initiatives" && renderInitiativeMenus()}
      {activeTab.label === "Milestones" && renderMilestoneMenus()}

      <div className="user-dashboard-all-objective-drawer-main-content">
        <div className="user-dashboard-all-objective-drawer-main-content-section">
          <div className="user-dashboard-content-tab">
            <div className="user-dashboard-content-tab-container">
              {tabData.map((tab) => (
                <button
                  key={tab.label}
                  className={`user-dashboard-content-tab-btn ${
                    activeTab.label === tab.label
                      ? "user-dashboard-content-tab-active"
                      : ""
                  }`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab.label}
                  <span className="tab-count">{tab.count}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="user-dashboard-content-tab-section">
            {renderTabContent()}
          </div>
        </div>
      </div>
                {isWarningOpen ? (
                  <WarningConfirmationPopup
                    isOpen={isWarningOpen}
                    onClose={() => setIsWarningOpen(false)}
                    onConfirm={() => setIsWarningOpen(false)}
                    title="Link Key Result"
                    description="Use global search to link this Key Result to an Objective and view it here."
                    confirmButtonText="Close"
                    cancelButtonText="Cancel"
                    isSingleButton={true}
                  />
                ) : (
                  ""
                )}
    </>
  );
};

export default UserDashboardAllObjectiveDrawerMainContent;
